// src/components/NewVehicleImageGallery.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Box, useMediaQuery, useTheme, CircularProgress, IconButton, Stack } from '@mui/material';
import { Image } from 'antd';
import {
    DownloadOutlined,
    SwapOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    UndoOutlined,
    PlayCircleFilled,
} from '@ant-design/icons';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface VehicleImage {
    vehicleImageUrl: string;
}

interface VehicleVideo {
    vehicleVideoUrl: string;
}

interface NewVehicleImageGalleryProps {
  vehicle: {
    vehicleImage: VehicleImage[];
    vehicleVideo?: VehicleVideo[];
  };
}

type MediaItem = {
    type: 'image' | 'video';
    url: string;
};

const NewVehicleImageGallery: React.FC<NewVehicleImageGalleryProps> = ({ vehicle }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- Prepare Data ---
    const videoList: MediaItem[] = (vehicle?.vehicleVideo || []).map((v) => ({
        type: 'video',
        url: v.vehicleVideoUrl,
    }));

    const imageList: MediaItem[] = (vehicle?.vehicleImage || []).map((i) => ({
        type: 'image',
        url: i.vehicleImageUrl,
    }));

    // Use the first image as the fallback background for videos
    const backdropImage = imageList.length > 0 ? imageList[0].url : '';

    const mediaList: MediaItem[] = [...videoList, ...imageList];
    const defaultIndex = 0;
    
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const [mediaLoading, setMediaLoading] = useState(true);
    const [videoPlaying, setVideoPlaying] = useState(false);

    const thumbnailContainerRef = useRef<HTMLDivElement>(null);
    const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

    const selectedMedia = mediaList[selectedIndex];

    // --- Handlers ---
    const handleNext = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedIndex((prev) => (prev + 1) % mediaList.length);
        setVideoPlaying(false);
    };

    const handlePrev = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedIndex((prev) => (prev - 1 + mediaList.length) % mediaList.length);
        setVideoPlaying(false);
    };

    const handleThumbnailClick = (index: number) => {
        setSelectedIndex(index);
        setVideoPlaying(false);
    };

    const handleMainMediaClick = () => {
        if (selectedMedia.type === 'video') {
            setVideoPlaying(true);
        } else {
            setLightboxVisible(true);
        }
    };

    const handleDownload = (url: string) => {
        const suffix = url.slice(url.lastIndexOf('.'));
        const filename = `vehicle-${Date.now()}${suffix}`;
        fetch(url)
            .then((res) => res.blob())
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                URL.revokeObjectURL(blobUrl);
                link.remove();
            });
    };

    // --- Effects ---

    // 1. Scroll active thumbnail into view
    useEffect(() => {
        const currentThumb = thumbnailRefs.current[selectedIndex];
        if (currentThumb && thumbnailContainerRef.current) {
            currentThumb.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    }, [selectedIndex]);

    // 2. Reset loading state when index changes
    useEffect(() => {
        setMediaLoading(true);
    }, [selectedIndex]);

    // 3. Preload the NEXT image
    useEffect(() => {
        const nextIndex = (selectedIndex + 1) % mediaList.length;
        const nextItem = mediaList[nextIndex];
        if (nextItem && nextItem.type === 'image') {
            const img = new globalThis.Image();
            img.src = nextItem.url;
        }
    }, [selectedIndex, mediaList]);

    // 4. Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (lightboxVisible || mediaList.length === 0) return;
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxVisible, mediaList.length]);

    const currentImageIndexInLightbox = imageList.findIndex(img => img.url === selectedMedia?.url);
    const initialImageIndex = currentImageIndexInLightbox >= 0 ? currentImageIndexInLightbox : 0;
    const iconStyle: React.CSSProperties = { color: '#fff', fontSize: 18, cursor: 'pointer' };

    if (mediaList.length === 0) {
      return (
        <Box sx={{ height: { xs: 250, sm: 400, md: 450 }, bgcolor: '#000', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#fff' }}>No Media Available</span>
        </Box>
      );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* --- Main Display Area --- */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 250, sm: 400, md: 450 },
                    backgroundColor: '#000',
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 3,
                    mb: 1.5,
                    '&:hover .nav-arrow': { opacity: 1 },
                }}
            >
                {/* Loading Spinner */}
                {mediaLoading && (
                    <Box sx={{ position: 'absolute', zIndex: 5 }}>
                        <CircularProgress sx={{ color: '#FFB800' }} />
                    </Box>
                )}

                {selectedMedia.type === 'image' && (
                    <Box
                        component="img"
                        src={selectedMedia.url}
                        alt="Vehicle"
                        onLoad={() => setMediaLoading(false)}
                        onClick={handleMainMediaClick}
                        decoding="async"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            bgcolor: '#000',
                            cursor: 'zoom-in',
                            transition: 'opacity 0.3s ease',
                            opacity: mediaLoading ? 0 : 1,
                        }}
                    />
                )}

                {selectedMedia.type === 'video' && (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: videoPlaying ? 'default' : 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                        onClick={handleMainMediaClick}
                    >
                        {/* BLURRED BACKDROP */}
                        {backdropImage && (
                            <Box
                                component="img"
                                src={backdropImage}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    filter: 'blur(20px) brightness(0.6)',
                                    transform: 'scale(1.1)',
                                    zIndex: 0,
                                }}
                            />
                        )}

                        {/* THE VIDEO */}
                        <Box
                            component="video"
                            src={selectedMedia.url}
                            playsInline 
                            webkit-playsinline="true"
                            controls={videoPlaying}
                            autoPlay={false}
                            muted={!videoPlaying}
                            onLoadedData={() => setMediaLoading(false)}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                zIndex: 1,
                                opacity: mediaLoading ? 0 : 1,
                                boxShadow: '0 0 20px rgba(0,0,0,0.5)',
                            }}
                        />
                        
                        {!videoPlaying && !mediaLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    zIndex: 4,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    borderRadius: '50%',
                                    p: { xs: 1.5, md: 2 },
                                    display: 'flex',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.1)', bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                            >
                                <PlayCircleFilled style={{ fontSize: isMobile ? 36 : 48, color: '#fff' }} />
                            </Box>
                        )}
                    </Box>
                )}

                {/* Navigation Arrows */}
                {mediaList.length > 1 && (
                    <>
                        <IconButton
                            className="nav-arrow"
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.7)',
                                color: '#000',
                                opacity: { xs: 1, md: 0 },
                                transition: 'all 0.3s',
                                '&:hover': { bgcolor: '#fff' },
                                zIndex: 10
                            }}
                        >
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            className="nav-arrow"
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.7)',
                                color: '#000',
                                opacity: { xs: 1, md: 0 },
                                transition: 'all 0.3s',
                                '&:hover': { bgcolor: '#fff' },
                                zIndex: 10
                            }}
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    </>
                )}
            </Box>

            {/* --- Thumbnails Strip --- */}
            <Stack 
                direction="row" 
                ref={thumbnailContainerRef}
                spacing={1.5} 
                sx={{ 
                    overflowX: 'auto', 
                    pb: 1, 
                    '&::-webkit-scrollbar': { height: 6 }, 
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 3 } 
                }}
            >
                {mediaList.map((item, idx) => {
                    const isActive = idx === selectedIndex;
                    return (
                        <Box
                            key={idx}
                            ref={(el) => { thumbnailRefs.current[idx] = el as HTMLDivElement | null; }}
                            onClick={() => handleThumbnailClick(idx)}
                            sx={{
                                flexShrink: 0,
                                width: { xs: 80, md: 100 }, 
                                height: { xs: 60, md: 75 },
                                borderRadius: 2,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                position: 'relative',
                                border: isActive 
                                    ? '2px solid #FFB800' 
                                    : '2px solid transparent',
                                opacity: isActive ? 1 : 0.6,
                                transition: 'all 0.2s ease',
                                '&:hover': { opacity: 1 },
                                bgcolor: '#f5f5f5',
                            }}
                        >
                            {item.type === 'video' ? (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        bgcolor: '#000',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundImage: backdropImage ? `url(${backdropImage})` : 'none',
                                        backgroundSize: 'cover',
                                    }}
                                >
                                    <Box sx={{position: 'absolute', inset:0, bgcolor:'rgba(0,0,0,0.5)'}} />
                                    <PlayCircleFilled style={{ color: '#fff', fontSize: isMobile ? 20 : 24, zIndex: 2 }} />
                                </Box>
                            ) : (
                                <Box
                                    component="img"
                                    src={item.url}
                                    loading="lazy"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Stack>

            {/* --- Ant Design Lightbox (Images Only) --- */}
            <Box sx={{ display: 'none' }}>
                <Image.PreviewGroup
                    preview={{
                        visible: lightboxVisible,
                        current: initialImageIndex,
                        onVisibleChange: (vis) => setLightboxVisible(vis),
                        onChange: (currentImageIdx) => {
                            const globalIndex = mediaList.findIndex(m => m.url === imageList[currentImageIdx].url);
                            if (globalIndex !== -1) setSelectedIndex(globalIndex);
                        },
                        toolbarRender: (
                            _,
                            { transform: { scale }, actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset } }
                        ) => (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    bottom: 50,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    padding: '10px 20px',
                                    borderRadius: 8,
                                    zIndex: 2000,
                                    display: 'flex',
                                    gap: 3,
                                }}
                            >
                                <DownloadOutlined style={iconStyle} onClick={() => handleDownload(mediaList[selectedIndex].url)} />
                                <SwapOutlined rotate={90} style={iconStyle} onClick={onFlipY} />
                                <SwapOutlined style={iconStyle} onClick={onFlipX} />
                                <RotateLeftOutlined style={iconStyle} onClick={onRotateLeft} />
                                <RotateRightOutlined style={iconStyle} onClick={onRotateRight} />
                                <ZoomOutOutlined style={iconStyle} onClick={onZoomOut} disabled={scale === 1} />
                                <ZoomInOutlined style={iconStyle} onClick={onZoomIn} disabled={scale === 50} />
                                <UndoOutlined style={iconStyle} onClick={onReset} />
                            </Box>
                        ),
                    }}
                >
                    {imageList.map((img, idx) => (
                        <Image key={idx} src={img.url} />
                    ))}
                </Image.PreviewGroup>
            </Box>
        </Box>
    );
};

export default NewVehicleImageGallery;